import { motion } from "framer-motion";
import {
  Brain,
  Globe,
  Smartphone,
  Cloud,
  Layers,
  Building2,
  Zap,
  TrendingUp,
} from "lucide-react";

// Persistent animations
import AnimatedBackground from "../components/AnimatedBackground";
import ParticleSystem from "../components/ParticleSystem";
import NeuralNetwork from "../components/NeuralNetwork";

// Core components
import Hero from "../components/Hero";
import ServiceCard from "../components/ServiceCard";
import Stats from "../components/Stats";
import AffiliationAccreditation from "../components/AffiliationAccreditation";
import Footer from "../components/Footer";
import XRSection from "../components/XRSection";

// Mock data / local list of services
const servicesList = [
  {
    icon: Brain,
    title: "Artificial Intelligence",
    description: "Custom machine learning models, natural language processing, predictive analysis, and generative AI pipelines.",
    color: "#7C4DFF",
  },
  {
    icon: Globe,
    title: "Web Development",
    description: "Premium, ultra-fast, and responsive SaaS platforms, corporate web systems, and interactive client portals.",
    color: "#27B8FF",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    description: "Cross-platform iOS and Android apps built with modern frameworks for seamless performance and offline capability.",
    color: "#A78BFA",
  },
  {
    icon: Cloud,
    title: "Cloud Solutions",
    description: "Scalable cloud native infrastructure, automated CI/CD pipelines, container orchestration, and serverless compute.",
    color: "#60A5FA",
  },
  {
    icon: Layers,
    title: "UI UX",
    description: "Premium user interfaces, sleek motion design, dynamic wireframes, and unified enterprise design systems.",
    color: "#F472B6",
  },
  {
    icon: Building2,
    title: "Enterprise Software",
    description: "Custom ERPs, CRMs, document automation systems, and high-security transactional business platforms.",
    color: "#10B981",
  },
  {
    icon: Zap,
    title: "Automation",
    description: "Trigger-based workflows, self-healing data pipelines, automated reporting, and smart task scheduling.",
    color: "#EC4899",
  },
  {
    icon: TrendingUp,
    title: "Digital Marketing",
    description: "Data-driven SEO optimization, conversion rate enhancement, visual branding, and analytics pipelines.",
    color: "#F59E0B",
  },
];

const Home = () => {
  return (
    <main className="overflow-hidden" style={{ background: "#060B1F" }}>
      {/* Background Animated Layer */}
      <AnimatedBackground />
      <ParticleSystem />
      <NeuralNetwork />

      {/* Hero Section */}
      <Hero />

      {/* Services Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-dense opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Our Core <span className="gradient-text">Services</span>
            </h2>
            <p className="text-slate-400 max-w-4xl lg:max-w-5xl mx-auto text-sm sm:text-[15px] lg:text-base leading-relaxed">
              We specialize in custom enterprise solutions and advanced AI systems built for modern businesses.
            </p>
          </motion.div>

          {/* Service Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesList.map((service, i) => (
              <ServiceCard
                key={service.title}
                index={i}
                icon={service.icon}
                title={service.title}
                description={service.description}
                color={service.color}
              />
            ))}
          </div>
        </div>

        <div className="section-divider mt-6 sm:mt-8" />
      </section>

      {/* Extended Reality (AR / VR / XR) Section */}
      <XRSection />

      {/* Why ClientCode AI (Stats) */}
      <Stats />

      {/* Affiliation & Accreditation Carousel */}
      <AffiliationAccreditation />

      {/* Premium Footer */}
      <Footer />
    </main>
  );
};

export default Home;
